import * as Sentry from '@sentry/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { PROCESSED_NOTIFICATION_KINDS } from 'cpool-constants';
import { AGGREGATE_NAMES, HTTP_STATUS, LOAN_REQUEST_COMMANDS, LOAN_REQUEST_RESOLVERS } from 'cpool-constants';

import { APICommunicator } from '~/api/APICommunicator';
import useUserInfo from '~/hooks/useUserInfo';

type MarkNotificationProcessedInput =
    | { kind: typeof PROCESSED_NOTIFICATION_KINDS.ACTIVE_USER_SET; loanRequestId: string; setAt: number }
    | { kind: typeof PROCESSED_NOTIFICATION_KINDS.ASSIGNED_USER_ADDED; loanRequestId: string; addedAt: number }
    | { kind: typeof PROCESSED_NOTIFICATION_KINDS.RESUBMISSION; loanRequestId: string; resubmissionAt: number }
    | { kind: typeof PROCESSED_NOTIFICATION_KINDS.DOCUMENT; loanRequestId: string }
    | { kind: typeof PROCESSED_NOTIFICATION_KINDS.BANK_REPORT; loanRequestId: string; subscriptionIdentifier: string };

/**
 * Records a handled notification in the backend. Replaces the former localStorage write. The processing
 * user is carried in the payload (the framework's `_issuer` is stripped before read models run).
 */
export const useMarkNotificationProcessed = () => {
    const queryClient = useQueryClient();
    const userInfo = useUserInfo();

    return useMutation({
        mutationFn: async (input: MarkNotificationProcessedInput) => {
            const userId = userInfo?.id;
            if (!userId) return;
            const { loanRequestId, ...payload } = input;
            await APICommunicator.sendCommand({
                aggregateId: loanRequestId,
                aggregateName: AGGREGATE_NAMES.LOAN_REQUEST,
                commandName: LOAN_REQUEST_COMMANDS.markNotificationProcessed,
                payload: { ...payload, userId },
            });
        },
        // 400 = notification still active (expected); background callers → no toast
        onError: (error: Error & { status?: number }) => {
            if (error.status !== HTTP_STATUS.BAD_REQUEST) Sentry.captureException(error);
        },
        onSuccess: () =>
            Promise.all([
                queryClient.invalidateQueries({
                    queryKey: [AGGREGATE_NAMES.LOAN_REQUEST, LOAN_REQUEST_RESOLVERS.getProcessedNotifications],
                }),
                queryClient.invalidateQueries({
                    queryKey: [AGGREGATE_NAMES.LOAN_REQUEST, LOAN_REQUEST_RESOLVERS.getAddedAssignedUserLeads],
                }),
                queryClient.invalidateQueries({
                    queryKey: [AGGREGATE_NAMES.LOAN_REQUEST, LOAN_REQUEST_RESOLVERS.getActiveUserSetLeads],
                }),
            ]),
    });
};
